import { Button, Stack, Box } from '@customink/pigment-react';
import { styled } from '@customink/pigment-react/lib/themeCustomink';
import { Grid, Collapse } from '@mui/material';
import { MouseEventHandler } from 'react';
import { useAuth } from '~/contexts/Auth/AuthContext';
import { OrderSummary, OrderDetails } from '~/adapters/services/accounts/orders/types';
import { BoxVisibleAboveMd } from '~/components/Visibility/VisibleBox';
import { useOrderButtonConfigs } from '~/pages/OrderHistory/OrdersList/OrderCard/DesktopOrderContentRow/useOrderButtonConfigs';
import { OrderProductsCarousel } from '~/pages/OrderHistory/OrdersList/OrderCard/DesktopOrderContentRow/OrderProductsCarousel';
import { Loader } from '~/components/Loader/loader';
import { OrderDetail } from '../OrderDetail/OrderDetail';
import { OrderCardSummaryInfo } from '../OrderCardSummaryInfo/OrderCardSummaryInfo';
import { OrderDetailToggle } from '../../../components/OrderDetailToggle';

const fallbackActionsWidth = '300px';

interface OrderContentRowProps {
    order: OrderSummary;
    detailOpen: boolean;
    toggle: MouseEventHandler<HTMLElement>;
    singleDetail: boolean;
    isLoading: boolean;
    data?: OrderDetails;
    open: boolean;
}

const OrderContent = styled(Stack)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        padding: '20px 24px',
    },
}));

const OrderActions = styled(Stack)(() => ({
    width: fallbackActionsWidth,
    justifyContent: 'center',
}));

const OrderContentStack = styled(Stack)(() => ({
    flexGrow: 1,
}));

const OrderContentInfoGrid = styled(Grid)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(1),
}));

const OrderContentContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    width: '100%',
    borderRadius: '8px',
}));

export function DesktopOrderContentRow({
    order,
    detailOpen,
    singleDetail,
    toggle,
    isLoading,
    data,
    open,
}: OrderContentRowProps) {
    const { isImpersonatedExperience } = useAuth();
    const buttonConfigs = useOrderButtonConfigs(order, isImpersonatedExperience);

    return (
        <OrderContentContainer>
            <OrderContent>
                <OrderContentStack direction="row">
                    <OrderProductsCarousel order={order} />
                    <OrderContentInfoGrid item>
                        <Stack sx={{ height: '100%' }}>
                            <OrderCardSummaryInfo order={order} />
                            <BoxVisibleAboveMd>
                                <OrderDetailToggle singleDetail={singleDetail} toggle={toggle} open={detailOpen} />
                            </BoxVisibleAboveMd>
                        </Stack>
                    </OrderContentInfoGrid>
                </OrderContentStack>
                <OrderActions spacing={1}>
                    {buttonConfigs.map((config) => (
                        <Button key={config.type} variant={config.variant} href={config.href} onClick={config.onClick}>
                            {config.label}
                        </Button>
                    ))}
                </OrderActions>
            </OrderContent>
            <Collapse in={open && !singleDetail} unmountOnExit>
                {isLoading && <Loader title="Loading..." />}
                {data && <OrderDetail orderDetails={data} />}
            </Collapse>
        </OrderContentContainer>
    );
}
